import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, Subject } from "rxjs";
import { map, tap } from 'rxjs/operators';
import { Router } from "@angular/router";

import { Team } from "./team.model";
import { environment } from "../../environments/environment";

const BACKEND_URL = environment.apiUrl + "/teams/";

@Injectable({ providedIn: "root" })
export class TeamsService {
  private teams: Team[] = [];
  private teamsUpdated = new Subject<{teams: Team[], total: number}>();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  importTeams(jsonData: any) {
    this.http
      .post(BACKEND_URL + 'import', {dataToImport: jsonData})
      .subscribe(responseData => {
        console.log('importTeams responseData: ', responseData);
        
        this.router.navigate(["/teams"]);
      });
  }

  /** One-shot fetch; does not update `getTeamUpdateListener()` state. */
  fetchTeamsPage(
    pageSize: number,
    currentPage: number,
    payload?: any
  ): Observable<{ teams: Team[]; total: number }> {
    let queryParams = `?pagesize=${pageSize}&page=${currentPage}`;
    const season_id = payload && payload.season_id;
    const season = payload && payload.season;
    const year = payload && payload.year && parseInt(payload.year);
    const division = payload && payload.division;

    if (season_id) {
      queryParams += `&season_id=${season_id}`;
    } else if (season) {
      queryParams += `&season_id=${season}`;
    }
    if (year) {
      queryParams += `&year=${year}`;
    }
    if (division) {
      queryParams += `&division_id=${division}`;
    }

    const url = payload?.admin ? BACKEND_URL + 'admin' + queryParams : BACKEND_URL + queryParams;
    return this.http.get<{ message: string; teams: any; total: number }>(url).pipe(
      map((teamData) => ({
        teams: teamData.teams.map((team) => ({
          id: team._id,
          name: team.name,
          division_id: team.division_id,
          season_id: team.season_id,
          year: team.year,
          captain1: team.captain1,
          captain2: team.captain2,
          captainId: team.captainId,
          coCaptainId: team.coCaptainId,
        })),
        total: teamData.total,
      }))
    );
  }

  getTeams(pageSize: number, currentPage: number, payload?: any) {
    this.fetchTeamsPage(pageSize, currentPage, payload).subscribe((transformedTeams) => {
      this.teams = transformedTeams.teams;
      this.teamsUpdated.next({ teams: [...this.teams], total: transformedTeams.total });
    });
  }

  getTeam(id: string) {
    return this.http.get<{ 
      _id: string;
      name: string;
      division_id: string;
      season_id: string;
      year: number;
      captain1: string;
      captain2: string;
      captainId?: string;
      coCaptainId?: string;
    }>(
      BACKEND_URL + id
    );
  }

  getTeamOverview(id: string) {
    return this.http.get<{
      message?: string;
      overview: {
        team: {
          _id: string;
          name: string;
          division_id: string;
          season_id: string;
          year: number;
          captain1: string;
          captain2: string;
          captainId?: string;
          coCaptainId?: string;
        };
        season: {
          id: string;
          name: string;
          year: string | number;
          tip_off?: string | null;
          early_bird_fee?: number | null;
          team_fee?: number | null;
        } | null;
        rosterCount: number;
        feeSplitMemberCount?: number;
        payment: {
          isFullyPaid: boolean;
          totalPaid: number;
          depositPaid?: number;
          amountDue: number | null;
          balanceDue: number | null;
          hasAnyPaidRecord: boolean;
          captainDepositOnlyPhase?: boolean;
          assignedAmountsByPlayerId?: Record<string, number>;
          /** Manual admin "Team fee" amounts by attributed user id (also merged into assignedAmounts). */
          manualTeamFeePaidByPlayerId?: Record<string, number>;
          rosterSharePaidByPlayerId?: Record<string, number>;
          drafteeCreditByPlayerId?: Record<string, number>;
          captainAssignedOutboundTotal?: number;
          captainFairShareAmount?: number | null;
          captainAssignableRemaining?: number | null;
          baseFairShareAmount?: number | null;
          fairShareByPlayerId?: Record<string, number>;
          feeDiscountByPlayerId?: Record<string, number>;
          teamFeeBreakdown?: {
            total: number;
            teamFee: number;
            jerseyQuantity: number;
            jerseyTotal: number;
          } | null;
        };
      };
    }>(`${BACKEND_URL}${id}/overview`, { withCredentials: true });
  }

  assignCaptainPaymentAllocations(teamId: string, body: { allocations: Array<{ userId: string; amount: number }> }) {
    return this.http.post<{ message: string; created: number }>(
      `${BACKEND_URL}${teamId}/payment-assignments`,
      body,
      { withCredentials: true }
    );
  }

  getTeamUpdateListener() {
    return this.teamsUpdated.asObservable();
  }

  addTeam(
    name: string,
    divisionId: string,
    season_id: string,
    captain1: string,
    captain2: string,
    captainId?: string,
    skipRedirect?: boolean,
    onCreated?: (team: Team) => void
  ) {
    const team: Team = { 
      id: null, 
      name: name, 
      division_id: divisionId,
      season_id: season_id,
      year: null,
      captain1: captain1,
      captain2: captain2,
      ...(captainId && { captainId })
    };
    this.http
      .post<{ message: string, teamId: string, total: number }>(BACKEND_URL, team)
      .subscribe(responseData => {
        const id = responseData.teamId;
        team.id = id;
        this.teams.push(team);
        this.teamsUpdated.next({teams: [...this.teams], total: responseData.total});
        onCreated?.(team);
        if (!skipRedirect) {
          this.router.navigate(["/teams"]);
        }
      });
  }

  updateTeam(
    id: string,
    name: string,
    divisionId: string,
    season_id: string,
    captain1: string,
    captain2: string,
    captainId?: string | null,
    coCaptainId?: string | null,
    skipRedirect?: boolean
  ) {
    const team: Team = { 
      id: id, 
      name: name, 
      division_id: divisionId,
      season_id: season_id,
      year: null,
      captain1: captain1,
      captain2: captain2,
      ...(captainId !== undefined && { captainId }),
      ...(coCaptainId !== undefined && { coCaptainId })
    };

    return this.http.put(BACKEND_URL + id, team).pipe(
      tap(() => {
        const updatedTeams = [...this.teams];
        const oldTeamIndex = updatedTeams.findIndex(t => t.id === team.id);
        if (oldTeamIndex >= 0) {
          updatedTeams[oldTeamIndex] = { ...updatedTeams[oldTeamIndex], ...team };
        }
        this.teams = updatedTeams;
        this.teamsUpdated.next({teams: [...this.teams], total: updatedTeams.length});
        if (!skipRedirect) {
          this.router.navigate(["/teams"]);
        }
      })
    );
  }

  deleteTeam(teamId: string) {
    this.http.put(BACKEND_URL + "delete/" + teamId, teamId)
      .subscribe(() => {
        const updatedTeams = this.teams.filter(t => t.id !== teamId);
        this.teams = updatedTeams;
        this.teamsUpdated.next({teams: [...this.teams], total: updatedTeams.length});
      });
  }

  checkExistingNameDivision(
    name: string, 
    divisionId: string,
    season_id: string,
    id?: string
  ) {
    return this.http.post<{exists: boolean}>(BACKEND_URL + "check-name-division", {
      id: id, 
      name: name, 
      division_id: divisionId,
      season_id: season_id
    });
  }
}
